var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { MessageHandler } from "../module";
import { STTCommandMessage, STTMessage, STTRegisterCommand, STTUnregisterCommand } from "../../messages/stt/STTMessages";
import { ModuleId } from "flavr-common";
import { NewModule } from "../module/NewModule";
/**
 * Parses recognized speech text into structured STT command messages so voice-driven features can consume semantic commands instead of raw transcripts.
 *
 * Messages:
 * - `STTMessage` - Provides recognition results that should be parsed into command messages.
 * - `STTRegisterCommand` - Lets other modules contribute supported voice commands at runtime.
 * - `STTUnregisterCommand` - Lets other modules remove supported voice commands at runtime.
 */
export class SpeechParser extends NewModule {
    /**
     * Registers the built-in voice commands ("create", "label") available for parsing.
     */
    constructor() {
        super(ModuleId.SPEECH_PARSER);
        this.commands = [];
        this.commandsNameToIndex = {};
        this.initCommands();
    }
    /// Message handlers ///
    handleSendBestResult(msg) {
        let bestResult = {
            collectCmds: [],
            numParameters: 0,
            numCommands: 0
        };
        for (let i = 0; i < msg.results.length; ++i) {
            const collectCmds = this.parseText(msg.results[i].text);
            const numCommands = collectCmds.length;
            let numParameters = 0;
            for (const cmd of collectCmds) {
                numParameters += cmd.parameters.length;
            }
            if (!bestResult || numCommands > bestResult.numCommands || numParameters > bestResult.numParameters) {
                bestResult = { collectCmds, numCommands, numParameters };
            }
        }
        if (bestResult && bestResult.collectCmds.length > 0) {
            const messages = [];
            for (const cmd of bestResult.collectCmds) {
                const parameters = {};
                const command = this.commands[cmd.cmdIdx];
                for (const param of cmd.parameters) {
                    const paramName = command.parameters[param.paramIdx].name;
                    parameters[paramName] = {
                        name: paramName,
                        value: param.value,
                        matchWord: param.matchWord
                    };
                }
                messages.push(new STTCommandMessage(command.name, cmd.matchWord, parameters, msg.uid));
            }
            this.connection.sendMultiple(messages);
        }
    }
    handleSSTRegisterCommand(msg) {
        this.registerCommand(msg.cmd);
    }
    handleSSTUnregisterCommand(msg) {
        this.unregisterCommand(msg.cmdName);
    }
    /// Register and unregister commands ///
    /**
     * Registers a speech command definition so future recognition results can be matched against it.
     */
    registerCommand(cmd) {
        if (!this.commandsNameToIndex[cmd.name]) {
            this.commandsNameToIndex[cmd.name] = this.commands.length;
            this.commands.push(cmd);
        }
        else {
            console.warn(`[stt] Command ${cmd.name} already registered`);
        }
    }
    /**
     * Removes the speech command with the given name so it is no longer considered during parsing.
     */
    unregisterCommand(cmdName) {
        const cmdIdx = this.commandsNameToIndex[cmdName];
        if (cmdIdx) {
            const lastCmd = this.commands.pop();
            if (cmdIdx !== this.commands.length && lastCmd) {
                this.commands[cmdIdx] = lastCmd;
                this.commandsNameToIndex[lastCmd.name] = cmdIdx;
            }
            delete this.commandsNameToIndex[cmdName];
        }
    }
    /// Private helper methods ///
    initCommands() {
        this.registerCommand({
            name: "create",
            regexp: "erstelle|stelle|stellen|create",
            parameters: [
                {
                    name: "shape",
                    regexp: "shape",
                    required: true,
                    needsValue: true,
                    customs: [
                        { name: "box", regexp: "box|boxen|würfel|cube|klotz" },
                        { name: "cylinder", regexp: "cylinder|zylinder|walze" }
                    ]
                },
                {
                    name: "color",
                    regexp: "color|farbe",
                    needsValue: true,
                    customs: [
                        { name: "black", regexp: "black|schwarz|dunkel|dunkle|schwarze" },
                        { name: "white", regexp: "white|weiß|hell|helle|weiße" },
                        { name: "blue", regexp: "blue|blau|blaue" },
                        { name: "red", regexp: "red|rot|rote" },
                        { name: "green", regexp: "green|grün|grüne" }
                    ]
                },
                {
                    name: "size",
                    regexp: "size|größe",
                    needsValue: true,
                    valueType: "number"
                },
                { name: "selectable", regexp: "selectable|selektierbar" }
            ]
        });
        this.registerCommand({
            name: "label",
            regexp: "label|level",
            parameters: [
                {
                    name: "text",
                    regexp: "text",
                    required: true,
                    needsValue: true,
                    valueType: "string"
                },
                {
                    name: "align",
                    regexp: "align|ausrichtung",
                    needsValue: true,
                    customs: [
                        { name: "center", regexp: "center|mitte|mittig|drehpunkt" },
                        { name: "right", regexp: "right|rechts" },
                        { name: "left", regexp: "left|links" },
                        { name: "top", regexp: "top|oben" },
                        { name: "bottom", regexp: "bottom|unten" }
                    ]
                },
                {
                    name: "size",
                    regexp: "size|größe",
                    needsValue: true,
                    valueType: "number"
                }
            ]
        });
    }
    parseText(text) {
        // replace 0,5 to 0.5
        text = text.replace(/\,(?=\d)/g, ".");
        const words = text.split(/\s/);
        let lastCmdMatchIdx = -1;
        let startIdx = 0;
        let matchWord = "";
        const collectCmds = [];
        let wIdx = 0;
        // search for commands
        for (; wIdx < words.length; ++wIdx) {
            const word = words[wIdx];
            const cmdMatchIdx = this.checkCommand(word);
            if (cmdMatchIdx !== -1) {
                if (lastCmdMatchIdx !== -1) {
                    collectCmds.push({
                        cmdIdx: lastCmdMatchIdx,
                        startIdx,
                        endIdx: wIdx - 1,
                        matchWord,
                        parameters: []
                    });
                }
                lastCmdMatchIdx = cmdMatchIdx;
                startIdx = wIdx;
                matchWord = word;
            }
        }
        if (lastCmdMatchIdx !== -1) {
            collectCmds.push({
                cmdIdx: lastCmdMatchIdx,
                startIdx,
                endIdx: wIdx - 1,
                matchWord,
                parameters: []
            });
        }
        // Search for the parameters for the commands
        if (collectCmds.length > 0) {
            for (const cm of collectCmds) {
                const cmd = this.commands[cm.cmdIdx];
                let lastPMatchIdx = -1;
                let matchWordP = "";
                let value;
                let wIdx = cm.startIdx + 1;
                for (; wIdx <= cm.endIdx; ++wIdx) {
                    const word = words[wIdx];
                    const p = this.checkCommandParam(cm.cmdIdx, word);
                    const pMatchIdx = p[0];
                    if (pMatchIdx !== -1) {
                        const param = cmd.parameters[pMatchIdx];
                        if (!param.needsValue) {
                            lastPMatchIdx = -1;
                            value = undefined;
                        }
                        else if (param.customs) {
                            const customParamIdx = p[1];
                            if (customParamIdx !== -1) {
                                cm.parameters.push({
                                    paramIdx: pMatchIdx,
                                    matchWord: word,
                                    value: param.customs[customParamIdx].name
                                });
                                lastPMatchIdx = -1;
                                value = undefined;
                            }
                        }
                        else {
                            if (lastPMatchIdx !== -1) {
                                if (!param.needsValue || value) {
                                    cm.parameters.push({
                                        paramIdx: lastPMatchIdx,
                                        matchWord: matchWordP,
                                        value
                                    });
                                }
                                value = undefined;
                            }
                            lastPMatchIdx = pMatchIdx;
                            matchWordP = word;
                        }
                        // save the needed value until the next match
                    }
                    else if (lastPMatchIdx !== -1) {
                        const param = cmd.parameters[lastPMatchIdx];
                        if (param.needsValue) {
                            if (param.valueType === "number") {
                                if (!isNaN(word)) {
                                    value = parseFloat(word);
                                }
                            }
                            else if (param.valueType === "string") {
                                if (!value) {
                                    value = word;
                                }
                                else {
                                    value += " " + word;
                                }
                            }
                            else if (param.customs) {
                                const cpIdx = this.checkCommandCustomParam(cm.cmdIdx, lastPMatchIdx, word);
                                if (cpIdx !== -1) {
                                    value = param.customs[cpIdx].name;
                                    matchWordP = word;
                                }
                            }
                        }
                    }
                }
                if ((lastPMatchIdx !== -1 && !cmd.parameters[lastPMatchIdx].needsValue) || value) {
                    cm.parameters.push({
                        paramIdx: lastPMatchIdx,
                        matchWord: matchWordP,
                        value
                    });
                }
            }
        }
        // remove invalid commands
        for (let cmdIdx = collectCmds.length - 1; cmdIdx >= 0; --cmdIdx) {
            const collectCmd = collectCmds[cmdIdx];
            const command = this.commands[collectCmd.cmdIdx];
            for (let paramIdx = 0; paramIdx < command.parameters.length; ++paramIdx) {
                const param = command.parameters[paramIdx];
                if (param.required) {
                    let found = false;
                    for (let j = 0; j < collectCmd.parameters.length; ++j) {
                        if (paramIdx === collectCmd.parameters[j].paramIdx) {
                            found = true;
                            break;
                        }
                    }
                    if (!found) {
                        collectCmds.splice(cmdIdx, 1);
                        break;
                    }
                }
            }
        }
        return collectCmds;
    }
    checkCommand(word) {
        for (let cIdx = 0; cIdx < this.commands.length; ++cIdx) {
            const cmd = this.commands[cIdx];
            if (word.match(new RegExp(cmd.regexp, "i"))) {
                return cIdx;
            }
        }
        return -1;
    }
    checkCommandParam(cmdIdx, word) {
        const cmd = this.commands[cmdIdx];
        if (cmd) {
            for (let pIdx = 0; pIdx < cmd.parameters.length; ++pIdx) {
                const param = cmd.parameters[pIdx];
                const match = word.match(new RegExp(param.regexp, "i"));
                if (match) {
                    return [pIdx, -1];
                }
                if (param.customs) {
                    const cpIdx = this.checkCommandCustomParam(cmdIdx, pIdx, word);
                    if (cpIdx !== -1) {
                        return [pIdx, cpIdx];
                    }
                }
            }
        }
        return [-1, -1];
    }
    checkCommandCustomParam(cmdIdx, pIdx, word) {
        const cmd = this.commands[cmdIdx];
        if (cmd) {
            const param = cmd.parameters[pIdx];
            if (param && param.customs) {
                for (let cpIdx = 0; cpIdx < param.customs.length; ++cpIdx) {
                    const custom = param.customs[cpIdx];
                    if (word.match(new RegExp(custom.regexp, "i"))) {
                        return cpIdx;
                    }
                }
            }
        }
        return -1;
    }
}
__decorate([
    MessageHandler(STTMessage)
], SpeechParser.prototype, "handleSendBestResult", null);
__decorate([
    MessageHandler(STTRegisterCommand)
], SpeechParser.prototype, "handleSSTRegisterCommand", null);
__decorate([
    MessageHandler(STTUnregisterCommand)
], SpeechParser.prototype, "handleSSTUnregisterCommand", null);
